<div *ngIf="isSaving" class="fixed inset-0 flex items-center justify-center z-[100] backdrop-blur-sm bg-white/30">
  <i class="fas fa-spinner fa-spin text-5xl md:text-6xl text-[#006269]"></i>
</div>

<div class="flex flex-col h-screen w-full bg-gray-100 overflow-hidden font-sans">

  <header class="h-14 bg-white text-006269 flex items-center justify-between px-6 shrink-0">
    <div class="flex items-center gap-3">
      <i class="fas fa-file-invoice-dollar text-cyan-300 text-xl"></i>
      <h1 class="font-bold text-base tracking-wide uppercase">GST Invoice Builder</h1>
    </div>
  </header>

  <div class="flex flex-col lg:flex-row flex-1 overflow-hidden">

    <section class="flex-1 h-full overflow-y-auto bg-white p-3 lg:p-6 custom-scrollbar">
      <div class="max-w-7xl mx-auto space-y-4">

        <!-- SECTION 1: CUSTOMER DETAILS -->
        <div class="bg-blue-50/50 p-5 rounded-xl border border-blue-100 shadow-sm space-y-4">
          <div class="flex justify-between items-center border-b border-blue-200 pb-2">
            <h3 class="text-blue-700 font-bold text-[11px] uppercase tracking-widest">Customer Details</h3>
            <!-- CUSTOMER TYPE SWITCH -->
            <select [(ngModel)]="customerType"
              class="text-xs font-bold border-none bg-blue-100 rounded px-2 py-1 outline-none">
              <option value="Retailer">RETAILER (B2B)</option>
              <option value="Direct">DIRECT CUSTOMER (B2C)</option>
            </select>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- 1. Firm Name (searchable) -->
            <div>
              <label class="text-[11px] font-bold text-gray-700 uppercase">Firm Name <span
                  class="text-red-600">*</span></label>
              <input type="text" name="cName" [(ngModel)]="customerName" #nameRef="ngModel" required minlength="3"
                placeholder="Firm / Customer Name" (input)="onNameInput($event)"
                [ngClass]="{'border-red-500 bg-red-50': nameRef.invalid && nameRef.touched}"
                class="w-full mt-1 border-gray-300 rounded-lg p-2 text-sm outline-none focus:border-blue-500 shadow-sm">
              <p *ngIf="nameRef.invalid && nameRef.touched" class="text-[10px] text-red-600 mt-1 font-bold">Valid name
                is required (min 3 chars).</p>
            </div>

            <!-- 2. Mobile (searchable) -->
            <div>
              <label class="text-[11px] font-bold text-gray-700 uppercase">Mobile Number <span
                  class="text-red-600">*</span></label>
              <input type="number" appOnlyNumber name="mob" [(ngModel)]="mobile" #mobRef="ngModel" required
                pattern="^[0-9]{10}$" maxlength="10" placeholder="10 Digit Mobile" (input)="onMobileInput($event)"
                [ngClass]="{'border-red-500 bg-red-50': mobRef.invalid && mobRef.touched}"
                class="w-full mt-1 border-gray-300 rounded-lg p-2 text-sm outline-none focus:border-blue-500 shadow-sm" />
              <p *ngIf="mobRef.invalid && mobRef.touched" class="text-[10px] text-red-600 mt-1 font-bold">Enter a valid
                10-digit number.</p>
            </div>

            <!-- 3. GSTIN (Shown for Retailer) -->
            <div *ngIf="customerType === 'Retailer'">
              <label class="text-[11px] font-bold text-gray-700 uppercase">GST Number</label>
              <div class="flex mt-1 gap-2">
                <input type="text" name="gstin" [(ngModel)]="gstin" #gstRef="ngModel" maxlength="15" minlength="15"
                  placeholder="15 Digit GSTIN" [ngClass]="{'border-red-500 bg-red-50': gstRef.invalid && gstRef.dirty}"
                  class="flex-1 border-gray-300 rounded-lg p-2 text-sm uppercase outline-none focus:border-blue-500 shadow-sm">
                <button (click)="fetchGstDetails()" [disabled]="isFetchingGst" type="button"
                  class="px-4 bg-[#006269] text-white rounded-lg text-xs font-bold transition-all hover:bg-black">
                  <i *ngIf="isFetchingGst" class="fas fa-spinner fa-spin"></i>
                  <span *ngIf="!isFetchingGst">Fetch</span>
                </button>
              </div>
              <p *ngIf="gstRef.invalid && gstRef.dirty" class="text-[10px] text-red-600 mt-1 font-bold">GSTIN must be 15
                chars.</p>
            </div>

            <!-- STATE DROPDOWN (Shown for Direct) -->
            <div *ngIf="customerType === 'Direct'">
              <label class="text-[11px] font-bold text-gray-700 uppercase">Customer State <span
                  class="text-red-600">*</span></label>
              <select [(ngModel)]="stateId" (change)="onStateChange()"
                class="w-full mt-1 border border-gray-300 rounded-lg p-2 text-sm outline-none focus:border-blue-500 shadow-sm bg-white">
                <option [value]="null" disabled>-- Choose State --</option>
                <option *ngFor="let s of allStates" [value]="s.id">{{ s.name }}</option>
              </select>
            </div>

            <!-- PAN Input for Direct > 2 Lakhs -->
            <div *ngIf="customerType === 'Direct' && (grandTotal || 0) > 200000">
              <label class="text-[11px] font-bold text-gray-700 uppercase">PAN Card <span
                  class="text-red-600">*</span></label>
              <input type="text" name="cPan" [(ngModel)]="customerPan" maxlength="10" placeholder="ABCDE1234F"
                class="w-full mt-1 rounded-lg p-2 text-sm uppercase outline-none focus:border-blue-500 shadow-sm">
              <p *ngIf="customerPan.length !=10" class="text-[10px] text-red-600 mt-1 font-bold">Required for billing >
                ₹2 Lakhs</p>
            </div>
          </div>

          <div>
            <label class="text-[11px] font-bold text-gray-700 uppercase">Complete Billing Address <span
                class="text-red-600">*</span></label>
            <textarea name="addr" [(ngModel)]="address" #addrRef="ngModel" required minlength="5" rows="2"
              placeholder="Full Address" [ngClass]="{'border-red-500 bg-red-50': addrRef.invalid && addrRef.touched}"
              class="w-full mt-1 border-gray-300 rounded-lg p-2 text-sm outline-none focus:border-blue-500 shadow-sm"></textarea>
            <p *ngIf="addrRef.invalid && addrRef.touched" class="text-[10px] text-red-600 mt-1 font-bold">Address is
              required.</p>
          </div>
        </div>

        <div class="w-full py-3 px-4" *ngIf="filteredCustomers.length > 0">
          <p class="font-semibold text-sm sm:text-base lg:text-xl text-444 mb-4">Customer List</p>

          <div class="overflow-x-auto whitespace-nowrap hide-scrollbar mt-4">
            <table class="w-full table bg-white border border-[#D9D9D9] overflow-hidden">
              <thead class="bg-[#F1F8FF] border border-[#0C447D] text-444 text-xs sm:text-xs">
                <tr>
                  <th class="p-2 font-medium text-center border-r border-[#0C447D]">No.</th>
                  <th class="p-2 font-medium text-center border-r border-[#0C447D]">Firm name</th>
                  <th class="p-2 font-medium text-center border-r border-[#0C447D]">Mobile number</th>
                  <th class="p-2 font-medium text-center border-r border-[#0C447D]">Type</th>
                  <th class="p-2 font-medium text-center border-r border-[#0C447D]">Address</th>
                </tr>
              </thead>
              <tbody class="text-xs sm:text-xs" *ngIf="filteredCustomers.length > 0">
                <tr class="odd:bg-white even:bg-[#F2F2F2] text-[#5A5959]"
                  *ngFor="let customer of filteredCustomers; let i = index">
                  <td class="p-3 text-center">{{ i + 1 }}</td>
                  <td class="p-3 text-[#006269] font-bold cursor-pointer" (click)="selectCustomer(customer)">
                    {{ customer.user_trade_name || customer.billingname || customer.customer_name || customer.name || '-' }}
                  </td>
                  <td class="p-3">{{ customer.customer_mobile || customer.mobilenumber || '-'}}</td>
                  <td class="p-3">{{ customer.user_customer_type || '-' }}</td>
                  <td class="p-3">{{ getCustomerListAddress(customer) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- SECTION 2: INVOICE BASICS -->
          <div class="bg-emerald-50/50 p-5 rounded-xl border border-emerald-100 shadow-sm space-y-4">
            <h3
              class="text-emerald-700 font-bold text-[11px] uppercase tracking-widest border-b border-emerald-200 pb-1">
              Invoice Basics</h3>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="text-[11px] font-bold text-gray-700 uppercase">Inv No</label>
                <input readonly type="text" [(ngModel)]="invoiceNo"
                  class="w-full mt-1 border border-gray-300 rounded-lg p-2 text-sm bg-white/80 font-mono font-bold text-emerald-800 uppercase tracking-tighter">
              </div>
              <div>
                <label class="text-[11px] font-bold text-gray-700 uppercase">Date</label>
                <input type="text" [value]="currentDate" disabled
                  class="w-full mt-1 bg-white/80 border-gray-300 rounded-lg p-2 text-sm text-gray-500 font-bold">
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <!-- Cash Payment -->
              <div>
                <label class="text-[11px] font-bold text-gray-700 uppercase">Cash Amount</label>
                <div class="flex gap-2">
                  <input type="number" [(ngModel)]="cashAmount" placeholder="0.00"
                    class="w-1/2 mt-1 border border-gray-300 rounded-lg p-2 text-sm bg-white outline-none focus:border-emerald-500 shadow-sm">
                  <select [(ngModel)]="cashAccId"
                    class="w-1/2 mt-1 border border-gray-300 rounded-lg p-2 text-sm bg-white outline-none focus:border-emerald-500 shadow-sm">
                    <option [value]="null">Select Cash Acc</option>
                    <option *ngFor="let acc of cashAccounts" [value]="acc.acc_id">{{acc.acc_name}}</option>
                  </select>
                </div>
              </div>

              <!-- Bank Payment -->
              <div>
                <label class="text-[11px] font-bold text-gray-700 uppercase">Bank Amount</label>
                <div class="flex gap-2">
                  <input type="number" [(ngModel)]="bankAmount" placeholder="0.00"
                    class="w-1/2 mt-1 border border-gray-300 rounded-lg p-2 text-sm bg-white outline-none focus:border-emerald-500 shadow-sm">
                  <select [(ngModel)]="bankAccId"
                    class="w-1/2 mt-1 border border-gray-300 rounded-lg p-2 text-sm bg-white outline-none focus:border-emerald-500 shadow-sm">
                    <option [value]="null">Select Bank Acc</option>
                    <option *ngFor="let acc of bankAccounts" [value]="acc.acc_id">{{acc.acc_name}}</option>
                  </select>
                </div>
              </div>

              <!-- Online Payment -->
              <div>
                <label class="text-[11px] font-bold text-gray-700 uppercase">Online Amount</label>
                <div class="flex gap-2">
                  <input type="number" [(ngModel)]="onlineAmount" placeholder="0.00"
                    class="w-1/2 mt-1 border border-gray-300 rounded-lg p-2 text-sm bg-white outline-none focus:border-emerald-500 shadow-sm">
                  <select [(ngModel)]="onlineAccId"
                    class="w-1/2 mt-1 border border-gray-300 rounded-lg p-2 text-sm bg-white outline-none focus:border-emerald-500 shadow-sm">
                    <option [value]="null">Select Online Acc</option>
                    <option *ngFor="let acc of onlineAccounts" [value]="acc.acc_id">{{acc.acc_name}}</option>
                  </select>
                </div>
              </div>

              <!-- Card Payment -->
              <div>
                <label class="text-[11px] font-bold text-gray-700 uppercase">Card Amount</label>
                <div class="flex gap-2">
                  <input type="number" [(ngModel)]="cardAmount" placeholder="0.00"
                    class="w-1/2 mt-1 border border-gray-300 rounded-lg p-2 text-sm bg-white outline-none focus:border-emerald-500 shadow-sm">
                  <select [(ngModel)]="cardAccId"
                    class="w-1/2 mt-1 border border-gray-300 rounded-lg p-2 text-sm bg-white outline-none focus:border-emerald-500 shadow-sm">
                    <option [value]="null">Select Card Acc</option>
                    <option *ngFor="let acc of cardAccounts" [value]="acc.acc_id">{{acc.acc_name}}</option>
                  </select>
                </div>
              </div>
            </div>

          </div>


          <!-- SECTION 3: RATES -->
          <div class="bg-amber-50/50 p-5 rounded-xl border border-amber-100 shadow-sm space-y-4">
            <h3 class="text-amber-700 font-bold text-[11px] uppercase tracking-widest border-b border-amber-200 pb-1">
              Rates & Totals</h3>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="text-[11px] font-bold text-gray-700 uppercase">Silver Rate <span
                    class="text-red-600">*</span></label>
                <input type="number" name="sRate" [(ngModel)]="silverRate" #rateRef="ngModel" required min="1"
                  [ngClass]="{'border-red-500 bg-red-50': rateRef.invalid && rateRef.touched}"
                  class="w-full border-gray-300 rounded-lg p-2 text-sm outline-none focus:border-amber-500 shadow-sm">
              </div>
              <div>
                <label class="text-[11px] font-bold text-amber-800 uppercase">Grand Total <span
                    class="text-red-600">*</span></label>
                <input type="number" name="gTotal" [(ngModel)]="grandTotal" #totalRef="ngModel" required min="1"
                  [ngClass]="{'border-red-500 bg-red-50': totalRef.invalid && totalRef.touched}"
                  class="w-full border-amber-400 bg-white rounded-lg p-2 text-sm font-black text-amber-700 shadow-sm">
              </div>
            </div>
            <div
              class="flex justify-between bg-white/80 p-3 rounded-lg border border-dashed border-amber-200 text-[11px]">
              <span class="text-gray-600 font-medium uppercase tracking-tighter">Taxable: <b
                  class="text-gray-900 ml-1">₹{{ amountX }}</b></span>
              <span class="text-amber-800 font-medium uppercase tracking-tighter"><b class="ml-1">{{ quantity }}
                  Gm</b></span>
            </div>
          </div>
        </div>

        <button (click)="printInvoice()" [disabled]="isSaving"
          class="w-full py-4 bg-[#006269] text-white font-black rounded-2xl shadow-xl hover:bg-black transition-all uppercase tracking-[0.2em] text-sm flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed">
          <i class="fas" [ngClass]="isSaving ? 'fa-spinner fa-spin text-cyan-300 text-lg' : 'fa-print text-cyan-300 text-lg'"></i>
          {{ isSaving ? 'Saving Invoice...' : 'Generate & Print Invoice' }}
        </button>
      </div>
    </section>

    <!-- RIGHT: PREVIEW PANEL (EXACTLY AS PROVIDED - UNTOUCHED) -->
    <aside
      class="w-full lg:w-[450px] xl:w-[580px] h-1/2 lg:h-full bg-gray-300 border-t lg:border-t-0 lg:border-l border-gray-400 p-4 overflow-hidden flex flex-col items-center">
      <div class="shrink-0 mb-3 text-[10px] font-black text-gray-500 uppercase tracking-widest">Real-time Preview</div>
      <div class="w-full h-full overflow-auto custom-scrollbar flex justify-center bg-gray-400 p-2">
        <div class="origin-top transform scale-[0.38] sm:scale-[0.50] lg:scale-[0.52] xl:scale-[0.65] transition-all">

          <!-- START PRINT SECTION -->
          <div id="print-section" class="w-[850px] bg-white border-[4px] border-[#006269] shadow-2xl p-6 mb-20">
            <div class="border border-black overflow-hidden font-sans text-black bg-white">
              <div class="flex justify-between items-center px-6 py-2 border-b border-black">
                <div class="text-[16px] text-[#006269] font-bold">GSTIN: 06ACEFM7839B1Z9</div>
                <div class="uppercase tracking-widest text-[12px] font-bold">Tax Invoice</div>
              </div>
              <div class="relative px-8 py-6 border-b border-black text-center">
                <div class="absolute left-8 top-6 h-20 w-20 bg-[#006269]"
                  style="-webkit-mask: url('assets/images/rate-list/rate-list2.png') no-repeat center; -webkit-mask-size: contain; mask: url('assets/images/rate-list/rate-list2.png') no-repeat center; mask-size: contain;">
                </div>
                <h1 class="text-4xl font-bold text-[#006269] mb-1 uppercase tracking-tight">Meena Silver & Co</h1>
                <p class="text-[14px] text-gray-600 font-medium leading-tight">Ground floor, 199, Warn No 11, Masjid
                  Wali
                  Gali, Rania, Sirsa, Haryana</p>
                <div class="flex justify-center gap-6 mt-2 font-bold text-gray-800 text-[14px]">
                  <span>PAN: ACEFM7839B</span>
                  <span>Mob: 86838 36136</span>
                </div>
              </div>
              <div class="grid grid-cols-2 border-b border-black">
                <div class="border-r border-black p-6 min-h-[160px]">
                  <h2 class="italic font-bold underline text-[13px] mb-2">Party Details:</h2>
                  <div class="font-bold text-lg uppercase mb-1">{{ customerName || 'CUSTOMER NAME' }}</div>
                  <div class="text-[14px] leading-snug">
                    <p class="whitespace-pre-line text-gray-600">{{ address || 'Customer Address' }}</p>
                    <p *ngIf="customerType === 'Retailer'" class="mt-4"><span class="font-bold">GSTIN / UIN:</span> {{
                      gstin || 'N/A' }}</p>
                  </div>
                </div>
                <div class="p-6 text-[14px] space-y-1">
                  <div class="grid grid-cols-[140px_1fr] gap-x-1">
                    <strong>Invoice No.</strong> <span>: {{ invoiceNo }}</span>
                    <strong>Dated</strong> <span>: {{ currentDate }}</span>
                    <strong>Mode of Pay</strong> <span>: {{ paymentMode }}</span>
                    <strong *ngIf="pan">Party PAN</strong> <span *ngIf="pan" class="font-bold">: {{ pan }}</span>
                    <strong>Party Mobile</strong> <span>: {{ mobile }}</span>
                    <strong>Reverse Charge</strong> <span>: No</span>
                    <strong>Place of supply</strong> <span>: {{ placeOfSupply }}</span>
                  </div>
                </div>
              </div>
              <table class="w-full text-[13px]">
                <thead class="bg-[#006269] text-white font-bold">
                  <tr>
                    <th class="border-r border-white/20 p-3 w-12 text-center">S.N.</th>
                    <th class="border-r border-white/20 p-3 text-left px-4 italic">Description of Item</th>
                    <th class="border-r border-white/20 p-3 w-24 text-center">HSN</th>
                    <th class="border-r border-white/20 p-3 w-28 text-center">Qty/Gm</th>
                    <th class="border-r border-white/20 p-3 w-24 text-center">Rate/Gm</th>
                    <th class="p-3 w-32 text-right px-6">Amount(₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="h-16 align-middle border-b border-gray-300">
                    <td class="border-r border-black text-center">1.</td>
                    <td class="border-r border-black px-4 font-semibold italic">Silver Ornaments</td>
                    <td class="border-r border-black text-center">711311</td>
                    <td class="border-r border-black text-center font-bold text-xl">{{ quantity }}</td>
                    <td class="border-r border-black text-center">{{ silverRate }}</td>
                    <td class="p-3 text-right font-bold text-gray-900 px-6">{{ amountX }}</td>
                  </tr>
                </tbody>
                <tfoot class="border-t border-black text-[14px]">
                  <tr class="h-10">
                    <td colspan="5" class="border-r border-black text-right px-6 font-semibold">Taxable Amount</td>
                    <td class="p-2 text-right font-bold text-gray-800 px-6">₹ {{ amountX }}</td>
                  </tr>
                  <tr class="h-10 border-t border-gray-200">
                    <td colspan="5" class="border-r border-black text-right px-6 italic text-gray-500">{{ taxText1 }}
                    </td>
                    <td class="p-2 text-right font-semibold text-gray-800 px-6">{{ isInterstate ? cgst : igst }}</td>
                  </tr>
                  <tr *ngIf="isInterstate" class="h-10 border-t border-gray-100">
                    <td colspan="5" class="border-r border-black text-right px-6 italic text-gray-500">{{ taxText2 }}
                    </td>
                    <td class="p-2 text-right font-semibold text-gray-800 px-6">{{ sgst }}</td>
                  </tr>
                  <tr class="h-10 border-t border-black bg-gray-50">
                    <td colspan="5" class="border-r border-black text-right px-6 font-bold uppercase">Total</td>
                    <td class="p-2 text-right font-bold text-gray-900 px-6">₹ {{ total }}</td>
                  </tr>
                  <tr class="h-10 border-t border-gray-200">
                    <td colspan="5" class="border-r border-black text-right px-6 text-gray-500">
                      Adjustment (Round Off)
                    </td>

                    <td class="p-2 text-right font-bold px-6" [ngClass]="adjustmentClass">
                      + {{ adjustmentValue | number:'1.2-2' }}
                    </td>
                  </tr>
                  <tr class="border-t-2 border-black bg-cyan-50">
                    <td colspan="5"
                      class="border-r border-black text-right px-6 text-xl font-black uppercase text-[#006269]">Grand
                      Total</td>
                    <td class="p-3 text-right text-2xl font-black text-[#006269] px-6 whitespace-nowrap">₹ {{ grandTotal
                      }}</td>
                  </tr>
                </tfoot>
              </table>
              <div class="grid grid-cols-2 border-t border-black">
                <div class="border-r border-black p-6 text-[11px] text-gray-500 leading-tight">
                  <h4 class="font-bold underline mb-1 uppercase text-red-800 tracking-tighter">Terms & Conditions</h4>
                  <p class="font-bold">E. & O. E.</p>
                  <ol class="list-decimal ml-4">
                    <li>Goods once sold will not be taken back.</li>
                    <li>Subject to RANIA jurisdiction only.</li>
                  </ol>
                </div>
                <div class="flex flex-col justify-between p-6 text-center">
                  <p class="font-bold text-[14px] text-[#006269]">For Meena Silver & Co.</p>
                  <div class="mt-14">
                    <p
                      class="border-t border-dotted border-gray-400 pt-1 font-bold text-[11px] uppercase text-gray-400">
                      Authorized Signatory</p>
                  </div>
                </div>
              </div>
              <div class="bg-[#006269] text-white p-2 text-center text-[11px] font-bold uppercase">
                Bank Details: HDFC Bank, Rania A/C: 50200116332671, IFSC: HDFC0002327
              </div>
            </div>
          </div>
          <!-- END PRINT SECTION -->

        </div>
      </div>
    </aside>
  </div>
</div>